/**
 * Telegram User Accounts (MTProto) API binding pour le cockpit.
 *
 * Cyrano FastAPI expose les routes sous
 *   `/api/creators/{cyrano_uuid}/telegram-user-accounts/*`
 * (router `app/api/routes/creators_telegram_user_accounts.py`, auth WorkOS
 * JWT, ACL via `creators.workos_org_id`).
 *
 * Le cockpit identifie ses creators via Convex `_id` + slug. Le pont est le
 * slug (= `Creator.name` cote Postgres = `creators.slug` cote Convex), resolu
 * via `useCyranoCreatorIdBySlug` (cf `persona-traits/api.ts`).
 *
 * 7 endpoints — le login, et rien d'autre :
 *   Auth flow (le jeton `asid` EST le login_token du backend, dans le chemin)
 *   - POST /auth/start                (flow phone : envoyer le code)
 *   - POST /auth/{asid}/code          (flow phone : valider le code)
 *   - POST /auth/qr/start             (flow QR : recuperer l'URL)
 *   - GET  /auth/{asid}/qr/poll       (flow QR : poll status, ~2s)
 *   - POST /auth/{asid}/password      (2FA, partage entre phone et QR)
 *   CRUD comptes
 *   - GET  /                          (comptes du creator, 1 actif au plus)
 *   - DELETE /{accountId}             (soft-delete cote cockpit)
 *
 * Le profil, les photos et le health-check ont ete RETIRES avec le reste de
 * MTProto le 2026-08-09 et ne sont pas revenus : ils n'ont pas de backend, et
 * un bouton qui appelle une route inexistante est pire que pas de bouton.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api as cyranoApi } from "@/core/api/client";
import { useCyranoCreatorIdBySlug } from "@/panels/persona-traits/api";

// ─── Types ──────────────────────────────────────────────────────────────────

export type DisconnectReasonCode =
  | "flood_wait"
  | "auth_revoked"
  | "account_banned"
  | "password_needed"
  | "network_error"
  | "unknown";

export type QRPollStatus = "waiting" | "scanned" | "needs_2fa" | "expired";

export interface TelegramUserAccountResponse {
  id: string;
  creator_id: string;
  tg_user_id: number;
  tg_username: string | null;
  first_name: string | null;
  last_name: string | null;
  dc_id: number | null;
  phone_masked: string;
  is_active: boolean;
  last_seen_at: string | null;
  last_health_check_at: string | null;
  disconnect_reason_code: DisconnectReasonCode | null;
  created_at: string;
}

export interface StartPhoneLoginBody {
  creator_id: string; // body legacy, validé == path par le backend
  phone: string;
}

export interface StartPhoneLoginResponse {
  auth_session_id: string;
  dc_id: number | null;
  expires_at: string;
}

export interface SubmitCodeBody {
  code: string;
}

export interface SubmitCodeResponse {
  needs_2fa: boolean;
  account: TelegramUserAccountResponse | null;
}

export interface SubmitPasswordBody {
  password: string;
}

export interface StartQRLoginResponse {
  auth_session_id: string;
  qr_url: string;
  qr_expires_at: string;
  expires_at: string;
}

export interface PollQRResponse {
  status: QRPollStatus;
  account: TelegramUserAccountResponse | null;
  qr_url: string | null;
  qr_expires_at: string | null;
}

/**
 * Format des erreurs structurées renvoyées par le backend dans `detail`
 * de l'HTTPException. Le frontend dérive le message i18n à partir du `code`.
 */
export interface TelegramErrorDetail {
  code:
    | DisconnectReasonCode
    | "business_bot_required"
    | "auth_session_not_found"
    | "wrong_step"
    | "duplicate_account"
    | "creator_id_mismatch"
    | "no_free_proxy"
    | "proxy_unusable";
  detail: string;
  /** Le message du serveur — dit QUOI faire (ex. « aucun proxy libre en US »). */
  message?: string;
  retry_after_seconds?: number;
  tg_user_id?: number;
}

// ─── Service ────────────────────────────────────────────────────────────────

function basePath(creatorId: string): string {
  return `/api/creators/${creatorId}/telegram-user-accounts`;
}

export const telegramUserAccountsService = {
  list: (creatorId: string, includeInactive = false) =>
    cyranoApi.get<TelegramUserAccountResponse[]>(basePath(creatorId), {
      params: { include_inactive: includeInactive },
    }),

  startPhoneLogin: (creatorId: string, phone: string) =>
    cyranoApi.post<StartPhoneLoginResponse>(`${basePath(creatorId)}/auth/start`, {
      creator_id: creatorId,
      phone,
    }),

  submitCode: (creatorId: string, asid: string, code: string) =>
    cyranoApi.post<SubmitCodeResponse>(
      `${basePath(creatorId)}/auth/${asid}/code`,
      { code },
    ),

  startQRLogin: (creatorId: string) =>
    cyranoApi.post<StartQRLoginResponse>(`${basePath(creatorId)}/auth/qr/start`, {}),

  pollQRLogin: (creatorId: string, asid: string) =>
    cyranoApi.get<PollQRResponse>(`${basePath(creatorId)}/auth/${asid}/qr/poll`),

  submitPassword: (creatorId: string, asid: string, password: string) =>
    cyranoApi.post<TelegramUserAccountResponse>(
      `${basePath(creatorId)}/auth/${asid}/password`,
      { password },
    ),

  deleteAccount: (creatorId: string, accountId: string) =>
    cyranoApi.delete<void>(`${basePath(creatorId)}/${accountId}`),

};

// ─── React Query hooks (slug Convex → UUID Cyrano resolve interne) ─────────

export function useTelegramUserAccounts(slug: string | null | undefined) {
  const { cyranoId, isResolving } = useCyranoCreatorIdBySlug(slug);
  const query = useQuery({
    queryKey: ["telegram-user-accounts", cyranoId],
    queryFn: () => telegramUserAccountsService.list(cyranoId!),
    enabled: Boolean(cyranoId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  return {
    cyranoId,
    accounts: (query.data as TelegramUserAccountResponse[] | undefined) ?? [],
    isLoading: isResolving || query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
  };
}

export function useStartPhoneLogin(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: (phone: string) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.startPhoneLogin(cyranoId, phone);
    },
  });
}

export function useSubmitCode(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: (vars: { asid: string; code: string }) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.submitCode(cyranoId, vars.asid, vars.code);
    },
  });
}

export function useStartQRLogin(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: () => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.startQRLogin(cyranoId);
    },
  });
}

export function usePollQRLogin(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: (asid: string) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.pollQRLogin(cyranoId, asid);
    },
  });
}

export function useSubmitPassword(cyranoId: string | undefined) {
  return useMutation({
    mutationFn: (vars: { asid: string; password: string }) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.submitPassword(
        cyranoId,
        vars.asid,
        vars.password,
      );
    },
  });
}

export function useDeleteTelegramUserAccount(cyranoId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => {
      if (!cyranoId) throw new Error("creator not resolved");
      return telegramUserAccountsService.deleteAccount(cyranoId, accountId);
    },
    onSuccess: () => {
      if (cyranoId) {
        qc.invalidateQueries({ queryKey: ["telegram-user-accounts", cyranoId] });
      }
    },
  });
}

// ─── Helper : extraction d'erreur typée depuis une exception API ───────────

export function extractTelegramErrorDetail(err: unknown): TelegramErrorDetail | null {
  if (!err || typeof err !== "object") return null;
  const e = err as Record<string, unknown>;
  // Chercher detail à 3 emplacements possibles (selon la stack HTTP qui
  // wrap) : e.response.data.detail, e.data.detail, e.detail.
  const response = (e["response"] ?? {}) as Record<string, unknown>;
  const responseData = (response["data"] ?? {}) as Record<string, unknown>;
  const data = (e["data"] ?? {}) as Record<string, unknown>;
  const detail =
    responseData["detail"] ?? data["detail"] ?? e["detail"];
  if (detail && typeof detail === "object" && "code" in detail) {
    return detail as TelegramErrorDetail;
  }
  return null;
}
